/**
 * SetupWizardGuard Component
 * Route guard that shows the setup wizard for first-time users
 */

import React from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useProfile } from '@/hooks/useProfile';
import { SetupWizard } from './SetupWizard';
import { Loader2 } from 'lucide-react';

interface SetupWizardGuardProps {
  children: React.ReactNode;
}

/**
 * SetupWizardGuard
 *
 * Wraps protected routes and shows the setup wizard modal for first-time users.
 * The wizard appears as an overlay, so the underlying page still renders.
 *
 * Flow:
 * 1. User logs in → lands on /dashboard
 * 2. Guard fetches profile and checks hasCompletedSetup
 * 3. If false → shows SetupWizard modal
 * 4. On completion → modal closes, user sees Dashboard
 * 5. Future logins → wizard skipped, Dashboard shows directly
 */
export const SetupWizardGuard: React.FC<SetupWizardGuardProps> = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile(user?.id || '');

  // Don't block render while loading - show children with loading indicator
  if (authLoading || profileLoading) {
    return (
      <>
        {children}
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-terracotta-600 mx-auto mb-4" />
            <p className="text-espresso-500">Setting up your workspace...</p>
          </div>
        </div>
      </>
    );
  }

  // User not authenticated - let RequireAuth handle redirect
  if (!isAuthenticated || !user) {
    return <>{children}</>;
  }

  // Check if user needs to complete setup
  const needsSetup = profile && !profile.hasCompletedSetup && !profile.setupWizardProgress?.skippedAt;

  return (
    <>
      {children}
      {needsSetup && <SetupWizard />}
    </>
  );
};

export default SetupWizardGuard;
